import React, { useState, useEffect } from 'react';
import { db, auth } from '../lib/firebase';
import { doc, setDoc } from 'firebase/firestore';
import { GoogleAuthProvider, signInWithPopup, onAuthStateChanged, User } from 'firebase/auth';
import { handleFirestoreError, OperationType } from '../lib/firestore-errors';

const MigrasiKeFirebaseView: React.FC = () => {
  const [logs, setLogs] = useState<string[]>([]);
  const [isMigrating, setIsMigrating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsub();
  }, []);

  const loginFirebase = async () => {
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
    } catch (error) {
      console.error("Login failed", error);
      alert("Login Firebase gagal.");
    }
  };

  const addLog = (msg: string) => {
    setLogs((prev) => [...prev, `${new Date().toLocaleTimeString()} - ${msg}`]);
  };

  const syncToFirestore = async (collectionName: string, items: any[], idField: string = 'id') => {
    let completed = 0;
    for (const item of items) {
      try {
        const docId = String(item[idField] || Math.random().toString(36).substring(7));
        const sanitizePayload = { ...item };
        
        // Convert Prisma dates to timestamp (number) because our rules require a number
        const dateFields = ['createdAt', 'updatedAt', 'startTime', 'endTime', 'submittedAt'];
        for (const field of dateFields) {
            if (sanitizePayload[field]) {
                sanitizePayload[field] = new Date(sanitizePayload[field]).getTime();
            }
        }
        
        const docRef = doc(db, collectionName, docId);
        await setDoc(docRef, sanitizePayload);
      } catch (error) {
        handleFirestoreError(error, OperationType.WRITE, `${collectionName}`);
      }
      completed++;
      if (completed % 10 === 0) {
        setProgress((prev) => prev + (10 / items.length) * 10);
      }
    }
  };

  const handleMigration = async () => {
    if (!window.confirm("Apakah Anda yakin ingin memulai migrasi ke Firebase? Pastikan Anda adalah Admin!")) return;
    setIsMigrating(true);
    setLogs([]);
    setProgress(0);
    try {
      const collections = [
        { key: 'teachers', target: 'teachers' },
        { key: 'students', target: 'students' },
        { key: 'agendas', target: 'agendas' },
        { key: 'attendanceRecords', target: 'attendanceRecords' },
        { key: 'assessmentGrades', target: 'assessmentGrades' },
        { key: 'sasGrades', target: 'sasGrades' },
        { key: 'formativeGrades', target: 'formativeGrades' },
        { key: 'learningObjectives', target: 'learningObjectives' },
        { key: 'schedules', target: 'schedules' },
        { key: 'schoolSettings', target: 'schoolSettings' },
        { key: 'tugas', target: 'tugas' },
        { key: 'tugasSubmissions', target: 'tugasSubmissions' },
        { key: 'profileAccessLogs', target: 'profileAccessLogs' },
        { key: 'learningContents', target: 'learningContents' },
      ];

      for (let i = 0; i < collections.length; i++) {
        const col = collections[i];
        addLog(`Mengunduh ${col.key} dari PostgreSQL...`);
        const res = await fetch(`/api/export-sync`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ collection: col.key })
        });
        if (!res.ok) {
           const errText = await res.text();
           throw new Error(`Gagal unduh ${col.key}: ${res.status} ${errText}`);
        }
        const items = await res.json();
        
        if (items && items.length > 0) {
          addLog(`Memigrasikan koleksi: ${col.target} (${items.length} data)...`);
          await syncToFirestore(col.target, items);
          addLog(`✅ Selesai migrasi koleksi ${col.target}.`);
        } else {
          addLog(`ℹ️ Tidak ada data untuk ${col.target}. Diferensiasi dilewati.`);
        }
        setProgress(((i + 1) / collections.length) * 100);
      }

      setProgress(100);
      addLog("🚀 MIGRASI SELESAI DENGAN SUKSES!");
      alert("Seluruh data telah berhasil dipindahkan ke Firebase!");
    } catch (e: any) {
      addLog(`❌ TERJADI KESALAHAN: ${e.message}`);
      alert("Migrasi gagal. Periksa log!");
    }
    setIsMigrating(false);
  };

  return (
    <div className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100 mt-6">
      <div className="flex items-center gap-3 mb-6">
        <h3 className="text-xl font-bold text-slate-800">Migrasi Data ke Firebase</h3>
      </div>
      <p className="text-sm text-slate-500 mb-6">
        Gunakan menu ini untuk menyalin ulang semua data dari PostgreSQL (Prisma) ke Firestore.
        Anda membutuhkan hak akses Admin di Firestore untuk melakukan hal ini.
      </p>

      {!user ? (
        <div className="mb-6 bg-red-50 p-4 rounded-xl border border-red-200">
          <p className="text-red-700 font-bold mb-2">Anda belum login ke Firebase!</p>
          <p className="text-red-600 text-sm mb-4">Untuk memigrasikan data, Anda harus login terlebih dahulu.</p>
          <button 
            onClick={loginFirebase}
            className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-bold hover:bg-red-700 transition"
          >
            Login dengan Google
          </button>
        </div>
      ) : (
        <div className="mb-6 bg-emerald-50 p-4 rounded-xl border border-emerald-200">
          <p className="text-emerald-700 font-bold">Login sebagai: {user.email}</p>
        </div>
      )}

      <button 
        className="px-6 py-3 bg-amber-500 text-white rounded-xl font-bold hover:bg-amber-600 transition-all disabled:opacity-50"
        disabled={isMigrating || !user}
        onClick={handleMigration}
      >
        {isMigrating ? 'Sedang Memigrasikan...' : 'Mulai Migrasi Data ke Firebase'}
      </button>

      {isMigrating && (
        <div className="w-full bg-slate-100 rounded-full h-3 mt-6 overflow-hidden">
          <div className="bg-amber-500 h-3 rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
        </div>
      )}

      <div className="mt-6 p-4 bg-slate-900 text-emerald-400 font-mono text-xs rounded-xl h-48 overflow-y-auto w-full">
        {logs.length === 0 ? <span className="text-slate-500">Log migrasi akan tampil di sini...</span> : logs.map((l, i) => <div key={i} className="mb-1">{l}</div>)}
      </div>
    </div>
  );
};

export default MigrasiKeFirebaseView;
